import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { login } from "../services/api";

// --- Login Page ---
const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Portal toggle: "accreditation" or "offcampus"
  const [portal, setPortal] = useState("accreditation");
  // Status-check mini form state
  const [showStatusCheck, setShowStatusCheck] = useState(false);
  const [statusEmail, setStatusEmail] = useState(
    () => localStorage.getItem('orgpass_pending_email') || ''
  );

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await login({ slsu_email: email, password: password, portal: portal });
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      const role = response.data.user.role;
      if (role === "admin") {
        navigate("/admin/dashboard");
      } else {
        navigate("/org/dashboard");
      }
    } catch (err) {
      if (err.response) {
        if (err.response.status === 429) {
          setError("Too many login attempts. Please wait a minute and try again.");
        } else if (err.response.status === 401) {
          setError("Invalid email or password. Please try again.");
        } else if (err.response.status === 403) {
          setError(err.response.data?.detail || "Access denied. Please check your portal selection.");
        } else {
          setError("Login failed. Please try again.");
        }
      } else {
        setError("Unable to connect to the server. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 sm:p-6 lg:p-8 py-8"
      style={{
        background: "#054334",
        backgroundImage: "url(assets/login_image.png)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0" style={{ background: "rgba(6, 60, 40, 0.82)" }} />

      <div className="relative z-10 w-full max-w-[1200px] bg-white rounded-[24px] lg:rounded-[40px] shadow-2xl overflow-hidden flex flex-col lg:flex-row lg:h-[85vh] lg:min-h-[650px]">
        {/* Left Panel */}
        <div className="flex flex-col justify-center items-center w-full lg:w-[45%] px-6 sm:px-8 lg:px-12 py-8 lg:py-10 relative lg:min-h-full lg:overflow-y-auto">
          <div className="w-full max-w-[400px] flex flex-col justify-center my-auto">
            {/* Logo */}
            <div className="flex justify-center items-center space-x-2 mb-4 lg:mb-6">
              <img src="assets/osas-logo.png" alt="OrgPass" className="w-8 h-8" onError={(e) => { e.target.style.display = "none"; }} />
              <span className="font-bold text-gray-800 text-[18px]">OrgPass</span>
            </div>

            <h1 className="text-[32px] sm:text-[36px] lg:text-[40px] font-extrabold text-gray-900 mb-2 text-center">Welcome Back</h1>
            <p className="text-[14px] sm:text-[15px] text-gray-500 mb-6 text-center">Sign in to your account using your Institutional Email</p>

            {/* Portal Toggle */}
            <div className="mb-6">
              <div className="relative flex w-full rounded-xl p-1" style={{ background: "#f0f4f2" }}>
                <div
                  className="absolute top-1 bottom-1 rounded-[10px] shadow-sm"
                  style={{
                    background: "#054334",
                    width: "calc(50% - 4px)",
                    left: portal === "accreditation" ? "4px" : "calc(50%)",
                    transition: "left 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                <button
                  type="button"
                  id="portal-accreditation"
                  onClick={() => { setPortal("accreditation"); setError(""); }}
                  className="relative z-10 flex-1 py-2.5 px-3 text-[12px] font-bold rounded-[10px] text-center"
                  style={{ color: portal === "accreditation" ? "#fff" : "#5a7a6e", transition: "color 0.25s ease" }}
                >
                  Accreditation
                </button>
                <button
                  type="button"
                  id="portal-offcampus"
                  onClick={() => { setPortal("offcampus"); setError(""); }}
                  className="relative z-10 flex-1 py-2.5 px-3 text-[12px] font-bold rounded-[10px] text-center"
                  style={{ color: portal === "offcampus" ? "#fff" : "#5a7a6e", transition: "color 0.25s ease" }}
                >
                  Off-Campus Activity
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-[13px] font-medium text-center">{error}</div>
            )}

            <form onSubmit={handleSignIn} className="space-y-6">
              <div>
                <label className="block text-[14px] font-bold text-gray-800 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                  required
                />
              </div>

              <div>
                <label className="block text-[14px] font-bold text-gray-800 mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-[#f8fafc] border border-gray-100 rounded-xl px-5 py-4 pr-12 text-[15px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition"
                    required
                  />
                  {password.length > 0 && (
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none">
                      {showPassword ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <label className="flex items-center space-x-2 cursor-pointer select-none">
                  <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-gray-300 accent-[#086C53] cursor-pointer" />
                  <span className="text-[14px] text-gray-600 font-medium">Remember me</span>
                </label>
                <button type="button" onClick={() => navigate("/forgot-password")} className="text-[14px] text-gray-600 font-medium hover:text-[#086C53] transition-colors">Forgot Password</button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl text-[16px] font-bold text-white transition-colors shadow-md mt-6 disabled:opacity-60"
                style={{ background: "#054334" }}
                onMouseEnter={(e) => !loading && (e.currentTarget.style.background = "#065440")}
                onMouseLeave={(e) => !loading && (e.currentTarget.style.background = "#054334")}
              >
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </form>
          </div>

          <div className="mt-8 flex flex-col items-center w-full space-y-4">
            <p className="text-[14px] text-gray-500 font-medium">
              Don't have an account?{" "}
              <Link to="/signup" className="text-[#086C53] font-bold hover:underline transition-colors">Sign Up</Link>
            </p>

            <div className="w-full max-w-[400px]">
              {!showStatusCheck ? (
                <button type="button" onClick={() => setShowStatusCheck(true)} className="w-full text-[13px] text-gray-400 hover:text-[#086C53] font-medium transition-colors text-center">
                  Already applied? Check your application status →
                </button>
              ) : (
                <div className="bg-[#f8fafc] border border-gray-100 rounded-xl p-4 space-y-3">
                  <p className="text-[13px] font-semibold text-gray-700">Check Application Status</p>
                  <p className="text-[12px] text-gray-400">
                    Enter the email you used when you submitted your access request.
                  </p>
                  <div className="flex items-center gap-2 px-2 py-1.5 bg-[#086C53]/8 border border-[#086C53]/15 rounded-lg">
                    <svg className="w-3.5 h-3.5 text-[#086C53] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-[11px] text-[#065440] font-medium">
                      Checking status for: <strong>{portal === 'offcampus' ? 'Off-Campus Activity' : 'Accreditation / Re-accreditation'}</strong> portal
                    </p>
                  </div>
                  <input type="email" value={statusEmail} onChange={(e) => setStatusEmail(e.target.value)} placeholder="your@slsu.edu.ph" className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-[13px] text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#086C53]/30 transition" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowStatusCheck(false)} className="flex-1 py-2 rounded-lg text-[13px] font-semibold text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">Cancel</button>
                    <button
                      type="button"
                      disabled={!statusEmail}
                      onClick={() => navigate(`/request-status?email=${encodeURIComponent(statusEmail)}&application_type=${encodeURIComponent(portal)}`)}
                      className="flex-1 py-2 rounded-lg text-[13px] font-bold text-white transition-colors disabled:opacity-50"
                      style={{ background: '#054334' }}
                    >
                      Check Status
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Panel — Image */}
        <div
          className="hidden lg:flex flex-1 relative flex-col justify-start p-16 rounded-[32px] m-4 overflow-hidden"
          style={{
            backgroundColor: "#054334",
            backgroundImage: "linear-gradient(to bottom, #004030 10%, rgba(0, 64, 38, 0) 60%), url(assets/login_image.png)",
            backgroundSize: "100% 100%, cover",
            backgroundPosition: "top left, center",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundAttachment: "scroll, fixed",
          }}
        >
          <div className="relative z-10 ml-[-14px] mt-[-10px]">
            <p className="text-white text-[15px] font-bold uppercase tracking-[0.2em] mb-2">Welcome to</p>
            <h2 className="text-white text-[64px] font-extrabold leading-tight mb-4">OrgPass</h2>
            <p className="text-white/90 text-[16px] leading-relaxed max-w-[280px] font-medium">Official Website for OSAS Student Organization and Activities</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
